import { faEdit, faHistory, faInfoCircle, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { memo, useCallback, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AgGridAtom, DatePickerAtom, TextFieldAtom } from "../../../../components/atoms/atom";
import ModalAtom from "../../../../components/atoms/modal/modal";
import {
    selectFormState,
    setFormField
} from "../../../../store/slices/invoiceV2Slice";
import { createActionColumn } from "../../../../utils/grid-columns";
import { formatCurrencyToLocale, getFormattedDate } from "../../../../utils/method";
import "./InvoiceSequenceConfiguration.scss";

const InvoiceSequenceConfiguration = memo(({ mode = "add" }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Redux state
    const formValues = useSelector(selectFormState);

    // Local state for historical invoice form
    const [showHistoricalForm, setShowHistoricalForm] = useState(false);
    const [editingHistoricalId, setEditingHistoricalId] = useState(null);
    const [historicalFormErrors, setHistoricalFormErrors] = useState({});
    const [historicalFormData, setHistoricalFormData] = useState({
        invoiceNumber: "",
        invoiceDate: null,
        billingPeriodFrom: null,
        billingPeriodTo: null,
        invoiceAmountNet: "0",
        aufmassAmountNet: "0",
        paymentAmountNet: "0",
        notes: ""
    });

    const readonly = formValues.readonly || false;
    const historicalInvoices = useMemo(
        () => formValues.historicalInvoices ?? [],
        [formValues.historicalInvoices],
    );

    // Historical data should be read-only once the invoice is saved (has an ID)
    const isHistoricalDataLocked = !!formValues.id;

    // Handle sequence type change
    const handleSequenceChange = useCallback((isHistoricalBaseline) => {
        dispatch(setFormField({ field: "isHistoricalBaseline", value: isHistoricalBaseline }));
        if (!isHistoricalBaseline) {
            // Clear historical invoices when switching back to normal
            dispatch(setFormField({ field: "historicalInvoices", value: [] }));
            setShowHistoricalForm(false);
        }
    }, [dispatch]);

    // Calculate totals from historical invoices
    const historicalTotals = useMemo(() => {
        if (!historicalInvoices || historicalInvoices.length === 0) {
            return { totalAmount: 0, count: 0, nextNumber: 1 };
        }

        const totalAmount = historicalInvoices.reduce((sum, inv) => {
            return sum + (parseFloat(inv.paymentAmountNet) || 0);
        }, 0);

        // Find max sequence number
        const maxSequence = Math.max(
            ...historicalInvoices.map((inv) => {
                const match = inv.invoiceNumber?.match(/(\d+)/);
                return match ? parseInt(match[1], 10) : 0;
            })
        );

        return {
            totalAmount,
            count: historicalInvoices.length,
            nextNumber: maxSequence + 1
        };
    }, [historicalInvoices]);

    // Handle add historical invoice button
    const handleAddHistorical = useCallback(() => {
        setEditingHistoricalId(null);
        setHistoricalFormErrors({});
        setHistoricalFormData({
            invoiceNumber: `AZ${historicalTotals.nextNumber}`,
            invoiceDate: null,
            billingPeriodFrom: null,
            billingPeriodTo: null,
            invoiceAmountNet: "0",
            aufmassAmountNet: "0",
            paymentAmountNet: "0",
            notes: ""
        });
        setShowHistoricalForm(true);
    }, [historicalTotals.nextNumber]);

    // Handle edit historical invoice
    const handleEditHistorical = useCallback((data) => {
        const rowData = data.data || data;
        setEditingHistoricalId(rowData.id);
        setHistoricalFormErrors({});
        setHistoricalFormData(rowData);
        setShowHistoricalForm(true);
    }, []);

    // Handle delete historical invoice
    const handleDeleteHistorical = useCallback((id) => {
        const updatedInvoices = historicalInvoices.filter(inv => inv.id !== id);
        dispatch(setFormField({ field: "historicalInvoices", value: updatedInvoices }));
    }, [historicalInvoices, dispatch]);

    // Update historical form data
    const updateHistoricalForm = useCallback((field, value) => {
        // Parse amount fields from comma-formatted strings to numbers
        const amountFields = ['invoiceAmountNet', 'aufmassAmountNet', 'paymentAmountNet'];
        let processedValue = value;

        if (amountFields.includes(field) && typeof value === 'string') {
            // Convert German locale format (comma as decimal) to number
            // e.g., "-113,91" -> -113.91
            const numericValue = value.replace(',', '.');
            const parsed = parseFloat(numericValue);
            processedValue = isNaN(parsed) ? value : parsed.toString();
        }

        // Handle validation logic for billing period dates
        if (field === "billingPeriodFrom" || field === "billingPeriodTo") {
            // Validate that billing dates are not after invoice date
            if (historicalFormData.invoiceDate) {
                if (field === "billingPeriodFrom" && value > historicalFormData.invoiceDate) {
                    setHistoricalFormErrors(prev => ({
                        ...prev,
                        billingPeriodFrom: "error.billing-period-from-after-invoice-date"
                    }));
                    return;
                }

                if (field === "billingPeriodTo" && value > historicalFormData.invoiceDate) {
                    setHistoricalFormErrors(prev => ({
                        ...prev,
                        billingPeriodTo: "error.billing-period-to-after-invoice-date"
                    }));
                    return;
                }
            }

            // Validate that billingPeriodTo is not before billingPeriodFrom
            if (field === "billingPeriodTo" && historicalFormData.billingPeriodFrom && value < historicalFormData.billingPeriodFrom) {
                setHistoricalFormErrors(prev => ({
                    ...prev,
                    billingPeriodTo: "error.billing-period-to-before-from"
                }));
                return;
            }

            // Validate that billingPeriodFrom is not after billingPeriodTo
            if (field === "billingPeriodFrom" && historicalFormData.billingPeriodTo && value > historicalFormData.billingPeriodTo) {
                setHistoricalFormErrors(prev => ({
                    ...prev,
                    billingPeriodFrom: "error.billing-period-from-after-to"
                }));
                return;
            }
        }

        setHistoricalFormData(prev => ({ ...prev, [field]: processedValue }));

        // Clear the error for this field if it exists
        if (historicalFormErrors[field]) {
            setHistoricalFormErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }

        // Also clear any related field errors
        if (field === "billingPeriodFrom" && historicalFormErrors.billingPeriodTo) {
            setHistoricalFormErrors(prev => ({
                ...prev,
                billingPeriodTo: ""
            }));
        }
        if (field === "billingPeriodTo" && historicalFormErrors.billingPeriodFrom) {
            setHistoricalFormErrors(prev => ({
                ...prev,
                billingPeriodFrom: ""
            }));
        }
        // Clear invoice date error if it was related to billing period
        if ((field === "billingPeriodFrom" || field === "billingPeriodTo") && historicalFormErrors.invoiceDate) {
            setHistoricalFormErrors(prev => ({
                ...prev,
                invoiceDate: ""
            }));
        }
    }, [historicalFormData.invoiceDate, historicalFormData.billingPeriodFrom, historicalFormData.billingPeriodTo, historicalFormErrors]);

    // Save historical invoice
    const handleSaveHistorical = useCallback(() => {
        // Validation
        const newErrors = {};

        if (!historicalFormData.invoiceNumber) {
            newErrors.invoiceNumber = "invoice.error-invoice-number-required";
        }
        if (!historicalFormData.invoiceDate) {
            newErrors.invoiceDate = "invoice.error-invoice-date-required";
        }
        if (!historicalFormData.billingPeriodFrom) {
            newErrors.billingPeriodFrom = "invoice.error-billing-period-from-required";
        }
        if (!historicalFormData.billingPeriodTo) {
            newErrors.billingPeriodTo = "invoice.error-billing-period-to-required";
        }
        if (!historicalFormData.aufmassAmountNet || parseFloat(historicalFormData.aufmassAmountNet) === 0) {
            newErrors.aufmassAmountNet = "invoice.error-aufmass-amount-required";
        }
        if (!historicalFormData.invoiceAmountNet || parseFloat(historicalFormData.invoiceAmountNet) === 0) {
            newErrors.invoiceAmountNet = "invoice.error-total-amount-required";
        }
        if (!historicalFormData.paymentAmountNet || parseFloat(historicalFormData.paymentAmountNet) === 0) {
            newErrors.paymentAmountNet = "invoice.error-current-invoice-amount-required";
        }

        if (Object.keys(newErrors).length > 0) {
            setHistoricalFormErrors(newErrors);
            return;
        }

        // Clear errors if validation passed
        setHistoricalFormErrors({});

        const newHistoricalInvoice = {
            id: editingHistoricalId || `hist-${Date.now()}`,
            ...historicalFormData,
        };

        let updatedInvoices;
        if (editingHistoricalId) {
            // Update existing
            updatedInvoices = historicalInvoices.map(inv =>
                inv.id === editingHistoricalId ? newHistoricalInvoice : inv
            );
        } else {
            // Add new
            updatedInvoices = [...historicalInvoices, newHistoricalInvoice];
        }

        dispatch(setFormField({ field: "historicalInvoices", value: updatedInvoices }));
        setShowHistoricalForm(false);
        setEditingHistoricalId(null);
    }, [historicalFormData, editingHistoricalId, historicalInvoices, dispatch]);

    // Cancel historical form
    const handleCancelHistorical = useCallback(() => {
        setShowHistoricalForm(false);
        setEditingHistoricalId(null);
        setHistoricalFormErrors({});
    }, []);

    // AG-Grid column definitions
    const historicalColumnDefs = useMemo(() => [
        {
            field: "invoiceNumber",
            headerName: t("invoice.invoice-number"),
            width: 60,
            sortable: true
        },
        {
            field: "invoiceDate",
            headerName: t("invoice.invoice-date"),
            width: 60,
            valueFormatter: (params) => getFormattedDate(params.value)
        },
        {
            headerName: t("invoice.billing-period"),
            width: 120,
            valueGetter: (params) => {
                const from = params.data?.billingPeriodFrom;
                const to = params.data?.billingPeriodTo;
                if (!from || !to) return "";
                return `${getFormattedDate(from)} - ${getFormattedDate(to)}`;
            },
            autoHeight: true,
            wrapText: true,
        },
        {
            field: "invoiceAmountNet",
            headerName: t("invoice.invoice-net-amount"),
            width: 140,
            type: "numericColumn",
            valueFormatter: (params) => formatCurrencyToLocale(params.value || 0),
        },
        {
            field: "aufmassAmountNet",
            headerName: t("invoice.aufmass-net-amount"),
            width: 140,
            type: "numericColumn",
            valueFormatter: (params) => formatCurrencyToLocale(params.value || 0),
        },
        {
            field: "paymentAmountNet",
            headerName: t("invoice.released-amount"),
            width: 140,
            type: "numericColumn",
            valueFormatter: (params) => formatCurrencyToLocale(params.value || 0),
        },
        {
            field: "notes",
            headerName: t("common.notes"),
            flex: 1,
            cellClass: 'fst-italic'
        },
        createActionColumn({
            t,
            actions: [
                {
                    onClick: handleEditHistorical,
                    label: "common.edit",
                    show: () => !isHistoricalDataLocked,
                },
                {
                    onClick: (data) => {
                        if (window.confirm(t("invoice.confirm-delete-historical"))) {
                            handleDeleteHistorical(data.id);
                        }
                    },
                    label: "common.delete",
                    danger: true,
                    show: () => !isHistoricalDataLocked,
                },
            ]
        })
    ], [t, handleEditHistorical, handleDeleteHistorical, isHistoricalDataLocked]);

    return (
        <div className="mb-3">
            <div>
                {isHistoricalDataLocked && (
                    <Alert variant="info" className="mb-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        {t("invoice.historical-data-locked-message")}
                    </Alert>
                )}

                {/* Radio buttons for sequence type */}
                <Form.Check
                    type="radio"
                    id="start-from-beginning"
                    name="sequenceType"
                    label={t("invoice.start-from-az1")}
                    checked={!formValues.isHistoricalBaseline}
                    onChange={() => handleSequenceChange(false)}
                    disabled={readonly || isHistoricalDataLocked}
                />
                <small className="text-muted d-block ms-4 mb-3">
                    {t("invoice.start-from-az1-hint")}
                </small>

                <Form.Check
                    type="radio"
                    id="continue-sequence"
                    name="sequenceType"
                    label={t("invoice.continue-from-previous")}
                    checked={formValues.isHistoricalBaseline}
                    onChange={() => handleSequenceChange(true)}
                    disabled={readonly || isHistoricalDataLocked}
                />
                <small className="text-muted d-block ms-4 mb-2">
                    {t("invoice.continue-from-previous-hint")}
                </small>

                {/* Historical invoices section */}
                {formValues.isHistoricalBaseline && (
                    <div className="mt-3 p-3 border rounded bg-light">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <strong>{t("invoice.historical-invoices-list")}</strong>
                            {!isHistoricalDataLocked && (
                                <Button size="sm" variant="primary" onClick={handleAddHistorical}>
                                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                                    {t("invoice.add-historical")}
                                </Button>
                            )}
                        </div>

                        {/* Historical invoices table */}
                        {historicalInvoices.length > 0 && (
                            <div className="isc-historical-grid">
                                <AgGridAtom
                                    columnDefs={historicalColumnDefs}
                                    rowData={historicalInvoices}
                                    domLayout="normal"
                                    rowHeight={40}
                                />
                            </div>
                        )}

                        {historicalInvoices.length === 0 && (
                            <Alert variant="info">
                                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                                {t("invoice.no-historical-invoices-yet")}
                            </Alert>
                        )}

                        {/* Summary */}
                        {historicalInvoices.length > 0 && (
                            <div className="mt-3 p-2 bg-white border rounded">
                                <Row>
                                    <Col md={6}>
                                        <small className="text-muted">
                                            <FontAwesomeIcon icon={faHistory} className="me-1" />
                                            {t("invoice.next-invoice-will-be")}:{" "}
                                            <strong className="text-primary">AZ-{historicalTotals.nextNumber}</strong>
                                        </small>
                                    </Col>
                                    <Col md={6} className="text-end">
                                        <small className="text-muted">
                                            {t("invoice.total-historical-amount")}:{" "}
                                            <strong className="text-success">{formatCurrencyToLocale(historicalTotals.totalAmount)}</strong>
                                        </small>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>
                )}

                {/* Modal for Add/Edit Historical Invoice */}
                <ModalAtom
                    show={showHistoricalForm}
                    handleClose={handleCancelHistorical}
                    handleSubmit={handleSaveHistorical}
                    title={
                        editingHistoricalId ? (
                            <>
                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                {t("invoice.edit-historical")}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                {t("invoice.add-historical")}
                            </>
                        )
                    }
                    saveText="common.save"
                    size="lg"
                    body={
                        <>
                            <Row className="mb-3">
                                <Col md={3}>
                                    <TextFieldAtom
                                        label={t("invoice.invoice-number")}
                                        value={historicalFormData.invoiceNumber}
                                        onChange={(e) => updateHistoricalForm('invoiceNumber', e.target.value)}
                                        required
                                        disabled
                                        error={historicalFormErrors.invoiceNumber ? t(historicalFormErrors.invoiceNumber) : ""}
                                    />
                                </Col>
                                <Col md={3}>
                                    <DatePickerAtom
                                        label={t("invoice.invoice-date")}
                                        selected={historicalFormData.invoiceDate}
                                        onChange={(date) => updateHistoricalForm('invoiceDate', date)}
                                        required
                                        error={historicalFormErrors.invoiceDate ? t(historicalFormErrors.invoiceDate) : ""}
                                    />
                                </Col>
                                <Col md={3}>
                                    <DatePickerAtom
                                        label={t("invoice.billing-period-from")}
                                        selected={historicalFormData.billingPeriodFrom}
                                        onChange={(date) => updateHistoricalForm('billingPeriodFrom', date)}
                                        required
                                        error={historicalFormErrors.billingPeriodFrom ? t(historicalFormErrors.billingPeriodFrom) : ""}
                                        maxDate={
                                            historicalFormData.invoiceDate && historicalFormData.billingPeriodTo
                                                ? historicalFormData.invoiceDate < historicalFormData.billingPeriodTo
                                                    ? historicalFormData.invoiceDate
                                                    : historicalFormData.billingPeriodTo
                                                : historicalFormData.invoiceDate || historicalFormData.billingPeriodTo
                                        }
                                    />
                                </Col>
                                <Col md={3}>
                                    <DatePickerAtom
                                        label={t("invoice.billing-period-to")}
                                        selected={historicalFormData.billingPeriodTo}
                                        onChange={(date) => updateHistoricalForm('billingPeriodTo', date)}
                                        required
                                        error={historicalFormErrors.billingPeriodTo ? t(historicalFormErrors.billingPeriodTo) : ""}
                                        maxDate={historicalFormData.invoiceDate}
                                        minDate={historicalFormData.billingPeriodFrom}
                                    />
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col md={3} className="isc-input-right">
                                    <TextFieldAtom
                                        type="number"
                                        label={t("invoice.invoice-net-amount")}
                                        value={historicalFormData.invoiceAmountNet}
                                        onChange={(e) => updateHistoricalForm('invoiceAmountNet', e.target.value)}
                                        required
                                        error={historicalFormErrors.invoiceAmountNet ? t(historicalFormErrors.invoiceAmountNet) : ""}
                                        suffix="€"
                                        min={undefined}
                                        decimalPlaces={2}
                                    />
                                </Col>
                                <Col md={3} className="isc-input-right">
                                    <TextFieldAtom
                                        type="number"
                                        label={t("invoice.aufmass-net-amount")}
                                        value={historicalFormData.aufmassAmountNet}
                                        onChange={(e) => updateHistoricalForm('aufmassAmountNet', e.target.value)}
                                        required
                                        error={historicalFormErrors.aufmassAmountNet ? t(historicalFormErrors.aufmassAmountNet) : ""}
                                        suffix="€"
                                        min={undefined}
                                        decimalPlaces={2}
                                    />
                                </Col>
                                <Col md={3} className="isc-input-right">
                                    <TextFieldAtom
                                        type="number"
                                        label={t("invoice.current-invoice-amount")}
                                        value={historicalFormData.paymentAmountNet}
                                        onChange={(e) => updateHistoricalForm('paymentAmountNet', e.target.value)}
                                        required
                                        error={historicalFormErrors.paymentAmountNet ? t(historicalFormErrors.paymentAmountNet) : ""}
                                        suffix="€"
                                        min={undefined}
                                        decimalPlaces={2}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>{t("common.notes")}</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={historicalFormData.notes}
                                            onChange={(e) => updateHistoricalForm('notes', e.target.value)}
                                            placeholder={t("invoice.historical-notes-placeholder")}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    }
                />
            </div>
        </div>
    );
});

InvoiceSequenceConfiguration.displayName = "InvoiceSequenceConfiguration";

export default InvoiceSequenceConfiguration;
